import { useMemo } from "react";

import {
  CellStateParams,
  PICKING_STATE,
  UseRangeSelectionParams
} from "../types";

export const useRangeSelection = ({
  startValue,
  endValue,
  hoverValue,
  picking,
  isDisabled,
  setHoverValue,
  cancelHoverClear
}: UseRangeSelectionParams) => {
  const range = useMemo(() => {
    if (startValue === null) {
      return {
        rangeStart: null,
        rangeEnd: null
      };
    }

    if (endValue !== null) {
      return {
        rangeStart: startValue,
        rangeEnd: endValue
      };
    }

    if (hoverValue !== null && picking === PICKING_STATE.End) {
      if (hoverValue < startValue) {
        return {
          rangeStart: startValue,
          rangeEnd: startValue
        };
      }

      return {
        rangeStart: startValue,
        rangeEnd: hoverValue
      };
    }

    return {
      rangeStart: startValue,
      rangeEnd: startValue
    };
  }, [startValue, endValue, hoverValue, picking]);

  const isPreview =
    startValue !== null && endValue === null && hoverValue !== null;

  const handleHover = (value: number) => {
    cancelHoverClear();

    if (
      picking !== PICKING_STATE.End ||
      startValue === null ||
      isDisabled(value)
    ) {
      return;
    }

    if (value < startValue) {
      setHoverValue(null);
      return;
    }

    setHoverValue(value);
  };

  const getCellState = ({ value, todayValue }: CellStateParams) => {
    const isSelected =
      value === range.rangeStart || (value === range.rangeEnd && !isPreview);

    const isInRange =
      range.rangeStart !== null &&
      range.rangeEnd !== null &&
      endValue !== null &&
      value > range.rangeStart &&
      value < range.rangeEnd;

    const isStart = value === range.rangeStart;

    const isEnd = value === range.rangeEnd && !isPreview && endValue !== null;

    const isPreviewEnd =
      isPreview &&
      value === range.rangeEnd &&
      range.rangeEnd !== range.rangeStart;

    const isToday = value === todayValue && !isSelected && !isInRange;

    return {
      isSelected,
      isInRange,
      isStart,
      isEnd,
      isPreviewEnd,
      isToday
    };
  };

  return {
    rangeStart: range.rangeStart,
    rangeEnd: range.rangeEnd,
    isPreview,
    handleHover,
    getCellState
  };
};
